import React, { useState, useEffect } from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

import DateInput from './DateInput';
import { apiGetSwishSyncDefaults, apiGetSwishSyncPreview, apiCommitSwishSync } from './Api';
import {
    SwishSyncPreview, SwishDecision, SwishDayPreview, SwishSplitRow, SyncRail,
    ZettleRailDecision, ZettlePayoutPreview, ZettleKassaPreview,
} from './Types';
import formatCurrency from './Formatting';

// Colour by decision: blue = will book (action), green = already done, grey = nothing,
// red = a flagged anomaly the treasurer must resolve.
const DECISION: Record<SwishDecision, { label: string, color: string }> = {
    nothing:      { label: 'Inget att göra',                color: '#888' },
    book:         { label: 'Bokförs',                       color: '#1565c0' },
    skip:         { label: 'Redan bokförd',                 color: '#2e7d32' },
    flag_excess:  { label: 'Flagga: överskott i clearing',  color: '#c62828' },
    flag_changed: { label: 'Flagga: ändrad efter bokföring', color: '#c62828' },
}

// The card and kassa rails share one set of verdicts (docs/zettle-swish.md §20-§21). Orange marks
// what is merely early — the payout is on its way — red what someone has to fix first.
const RAIL_DECISION: Record<ZettleRailDecision, { label: string, color: string }> = {
    nothing:  { label: 'Inget att göra',              color: '#888' },
    book:     { label: 'Bokförs',                     color: '#1565c0' },
    skip:     { label: 'Redan bokförd',               color: '#2e7d32' },
    external: { label: 'Bokförd utanför Eko',         color: '#6a1b9a' },
    waiting:  { label: 'Väntar på utbetalningen',     color: '#ed6c02' },
    outside:  { label: 'Insatt efter t.o.m.',         color: '#888' },
    blocked:  { label: 'Blockerad',                   color: '#c62828' },
    withheld: { label: 'Utanför ditt ansvarsområde',  color: '#888' },
}

// Per-transaction status → label + colour. Blue/green = will book, amber = needs attention,
// red = won't book (ambiguous/unmapped), grey = ignored (refund/blocked day).
// `rank` orders the expanded detail. A competition batch lists 1 400 deposits and 1 160 purchases,
// of which perhaps a dozen need a human — unsorted, those dozen are needles in a haystack, and the
// list's whole purpose is to surface them. So: anything that stops money booking first, then
// anything that merely needs looking at, then the booked bulk last.
const ITEM_STATUS: Record<string, { label: string, color: string, rank: number }> = {
    ambiguous:           { label: 'Tvetydig',           color: '#c62828', rank: 0 },
    unmapped:            { label: 'Omappad kategori',   color: '#c62828', rank: 0 },
    zero_value:          { label: 'Saknar belopp',      color: '#c62828', rank: 0 },
    blocked:             { label: 'Blockerad',          color: '#888',    rank: 1 },
    unknown_message:     { label: 'Okänt meddelande',   color: '#ed6c02', rank: 2 },
    unmatched:           { label: 'Utan match',         color: '#ed6c02', rank: 2 },
    registered_not_paid: { label: 'Saknar insättning',  color: '#ed6c02', rank: 2 },
    refund:              { label: 'Återbetalning (bokförs i kassan)', color: '#888', rank: 3 },
    externally_booked:   { label: 'Bokförd utanför Eko', color: '#6a1b9a', rank: 3 },
    covered_by_backup:   { label: 'Täcks av reservaffisch', color: '#2e7d32', rank: 3 },
    booked:              { label: 'Bokförs',            color: '#1565c0', rank: 4 },
    booked_message:      { label: 'Bokförs (meddelande)', color: '#1565c0', rank: 4 },
}

/** Attention first, then the largest amounts — the order someone scanning a batch reads in.
 *  An unlabelled status sorts with the bulk rather than vanishing; the label table is pinned by
 *  test_swish_status_labels, so that case should not arise. */
function byAttention<T extends { status: string, amount: number }>(items: readonly T[]): T[] {
    const rank = (s: string) => ITEM_STATUS[s]?.rank ?? 4
    return [...items].sort((a, b) => rank(a.status) - rank(b.status) || b.amount - a.amount)
}
const statusChip = (status: string) => {
    // The fallback renders the backend's own identifier, which is English and snake_case — so a
    // status added on the server and forgotten here surfaces as `unknown_message` to a Swedish
    // treasurer. Kept as a fallback rather than a crash (a missing label is never worth a blank
    // page), but it should never fire: test_swish_status_labels pins the table against the source.
    const s = ITEM_STATUS[status] ?? { label: status, color: '#555' }
    return <span style={{ color: s.color, fontWeight: 600, fontSize: '0.8em' }}>{s.label}</span>
}

const dayLabel = (d: string) => {
    const date = new Date(d + 'T00:00:00')
    return `${['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'][date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`
}

/** The split per account, greyed where it lies outside the role's scope. */
const SplitCell = ({ split }: { split: SwishSplitRow[] }) => <>{split.map((s, i) => (
    <span key={i} title={s.in_scope ? undefined : 'Utanför ditt ansvarsområde'}
          style={{ color: s.in_scope ? undefined : '#bbb' }}>
        {i > 0 && ', '}{s.income_account}: {formatCurrency(s.amount)}
    </span>))}</>

const RailStatus = ({ decision, flags }: { decision: ZettleRailDecision, flags: string[] }) => <>
    <span style={{ color: RAIL_DECISION[decision].color, fontWeight: 600 }}>{RAIL_DECISION[decision].label}</span>
    {flags.length > 0 && <span style={{ color: '#c62828', fontSize: '0.8em', marginLeft: '0.5rem' }}>⚠ {flags.length}</span>}
</>

const Flags = ({ flags }: { flags: string[] }) => <>{flags.map((flag, i) =>
    <div key={i} style={{ color: '#c62828', fontSize: '0.85em' }}>⚠ {flag}</div>)}</>

/** A rail's heading and its own Bokför button: the rails become ready at different times — the
 *  card payout lands a day after the weekend's Swish — and none waits for another. */
function RailHeading({ title, explanation, count, disabled, onBook }: {
    title: string, explanation: React.ReactNode, count: number, disabled: boolean, onBook: () => void,
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{title}</h3>
            <span style={{ color: '#666', fontSize: '0.85em', flex: '1 1 20rem' }}>{explanation}</span>
            <Button variant="contained" size="small" disabled={disabled || count === 0} onClick={onBook}>
                Bokför {title.toLowerCase()} ({count})
            </Button>
        </div>
    )
}

function PayoutTable({ payouts, expanded, toggle }: {
    payouts: ZettlePayoutPreview[], expanded: Set<string>, toggle: (key: string) => void,
}) {
    return (
        <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Utbetald</TableCell>
                        <TableCell>Insatt</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Brutto</TableCell>
                        <TableCell align="right">Avgift</TableCell>
                        <TableCell align="right">Netto</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {payouts.map(p => {
                        const key = `kort-${p.sent}-${p.payout}`
                        const isOpen = expanded.has(key)
                        const days = p.sales_days
                        return (
                            <React.Fragment key={key}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(key)}>
                                    <TableCell>
                                        <span style={{ display: 'inline-block', width: '1.2em', color: '#888' }}>{isOpen ? '▾' : '▸'}</span>
                                        {p.sent}
                                        {days.length > 0 &&
                                            <span style={{ color: '#777', marginLeft: '0.5em', fontSize: '0.85em' }}>
                                                (försäljning {days.length === 1 ? dayLabel(days[0]) : `${dayLabel(days[0])} – ${dayLabel(days[days.length - 1])}`})
                                            </span>}
                                    </TableCell>
                                    <TableCell>{p.bank_date ?? '–'}</TableCell>
                                    <TableCell><RailStatus decision={p.decision} flags={p.flags}/></TableCell>
                                    <TableCell align="right">{formatCurrency(p.gross)}</TableCell>
                                    <TableCell align="right">{formatCurrency(p.fee)}</TableCell>
                                    <TableCell align="right">{formatCurrency(p.payout)}</TableCell>
                                </TableRow>
                                {isOpen &&
                                    <TableRow>
                                        <TableCell/>
                                        <TableCell colSpan={5} sx={{ py: 1 }}>
                                            <div style={{ fontSize: '0.85em' }}>{p.payments} kortbetalningar · <SplitCell split={p.split}/></div>
                                            <Flags flags={p.flags}/>
                                        </TableCell>
                                    </TableRow>}
                            </React.Fragment>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function KassaTable({ days, expanded, toggle }: {
    days: ZettleKassaPreview[], expanded: Set<string>, toggle: (key: string) => void,
}) {
    return (
        <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Säljdag</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Kontant</TableCell>
                        <TableCell align="right">Presentkort</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {days.map(k => {
                        const key = `kassa-${k.date}`
                        const isOpen = expanded.has(key)
                        return (
                            <React.Fragment key={key}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(key)}>
                                    <TableCell>
                                        <span style={{ display: 'inline-block', width: '1.2em', color: '#888' }}>{isOpen ? '▾' : '▸'}</span>
                                        {k.date} <span style={{ color: '#777', fontSize: '0.85em' }}>({dayLabel(k.date)})</span>
                                    </TableCell>
                                    <TableCell><RailStatus decision={k.decision} flags={k.flags}/></TableCell>
                                    <TableCell align="right">{k.cash !== 0 ? formatCurrency(k.cash) : '–'}</TableCell>
                                    <TableCell align="right">{k.giftcards !== 0 ? formatCurrency(k.giftcards) : '–'}</TableCell>
                                </TableRow>
                                {isOpen &&
                                    <TableRow>
                                        <TableCell/>
                                        <TableCell colSpan={3} sx={{ py: 1 }}>
                                            <div style={{ fontSize: '0.85em' }}>
                                                {k.cash_sales} kontantköp
                                                {k.cash_refunds > 0 && `, ${k.cash_refunds} återbetalda`}
                                                {k.swish_refunds > 0 && `, ${k.swish_refunds} återbetalda Swish-köp`}
                                                {k.giftcard_count > 0 && `, ${k.giftcard_count} inlösta presentkort`}
                                                {' · '}<SplitCell split={k.split}/>
                                            </div>
                                            <Flags flags={k.flags}/>
                                        </TableCell>
                                    </TableRow>}
                            </React.Fragment>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

const yesterday = () => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d.toISOString().slice(0, 10)
}

/** What a batch covers beyond its own bank date, e.g. "(lör 10/10 – sön 11/10)".
 *
 * A row is a settlement batch, not a day: the bank stamps Friday-evening-to-Sunday payments with
 * Monday's date, so one row can hold a whole competition weekend. Without this the treasurer sees
 * a large sum appear on a Monday with nothing on the Saturday or Sunday, which reads as lost data.
 * Empty when everything sold on the bank date itself, which is every ordinary weekday. */
function batchCovers(day: SwishDayPreview): string {
    const days = (day.sales_days ?? []).filter(d => d !== day.date)
    if (days.length === 0) return ''
    const label = (d: string) => {
        const date = new Date(d + 'T00:00:00')
        return `${['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'][date.getDay()]} ${date.getDate()}/${date.getMonth() + 1}`
    }
    return days.length === 1 ? `(${label(days[0])})` : `(${label(days[0])} – ${label(days[days.length - 1])})`
}

// The expanded detail for one day: every deposit and every Zettle purchase, plus the flag texts.
function DayDetail({ day }: { day: SwishDayPreview }) {
    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.5rem', padding: '0.25rem 0 0.5rem 0.5rem' }}>
            {day.deposits.length > 0 &&
                <div>
                    <div style={{ fontWeight: 600, fontSize: '0.8em', color: '#555' }}>
                        Insättningar ({day.deposits.length})
                    </div>
                    {byAttention(day.deposits).map((d, i) =>
                        <div key={`d${i}`} style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85em' }}>
                            <span style={{ width: '6rem', textAlign: 'right' }}>{formatCurrency(d.amount)}</span>
                            <span style={{ flex: 1, color: '#333' }}>{d.reference || <em style={{ color: '#999' }}>utan referens</em>}</span>
                            {statusChip(d.status)}
                        </div>)}
                </div>}
            {day.purchases.length > 0 &&
                <div>
                    <div style={{ fontWeight: 600, fontSize: '0.8em', color: '#555' }}>
                        Zettle-köp ({day.purchases.length})
                    </div>
                    {byAttention(day.purchases).map((p, i) =>
                        <div key={`p${i}`} style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85em' }}>
                            <span style={{ width: '6rem', textAlign: 'right' }}>{formatCurrency(p.amount)}</span>
                            <span style={{ flex: 1, color: '#333' }}>
                                {p.label}
                                {p.sales_day && p.sales_day !== day.date &&
                                    <span style={{ color: '#777', marginLeft: '0.5em' }}>({p.sales_day})</span>}
                            </span>
                            {statusChip(p.status)}
                        </div>)}
                </div>}
            {day.flags.map((flag, i) =>
                <div key={`f${i}`} style={{ color: '#c62828', fontSize: '0.85em' }}>⚠ {flag.detail}</div>)}
        </div>
    )
}

const SwishSync = ({ active }: { active?: boolean }) => {
    // Slow (a fiscal year of bank + Zettle reads), so the preview is fetched only on demand.
    // fromDate defaults to the fiscal-year start: on first activation a tiny /sync/defaults call
    // fills it in (the client can't know the fiscal year on its own), so narrowing the window
    // (e.g. to skip a past, already-booked event whose poster message was since repointed) is a
    // one-field edit from there.
    const [fromDate, setFromDate] = useState('')
    const [cutoff, setCutoff] = useState(yesterday())
    const [preview, setPreview] = useState<SwishSyncPreview | null>(null)
    const [loading, setLoading] = useState(false)
    const [busy, setBusy] = useState(false)   // a commit is running
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [defaultsLoaded, setDefaultsLoaded] = useState(false)

    useEffect(() => {
        if (!active || defaultsLoaded) return
        setDefaultsLoaded(true)
        apiGetSwishSyncDefaults()
            .then(d => { if (d.from_date) setFromDate(prev => prev || d.from_date!) })
            .catch(() => setDefaultsLoaded(false))   // let a later activation retry
    }, [active, defaultsLoaded])

    const loadPreview = async () => {
        setLoading(true)
        try {
            const p = await apiGetSwishSyncPreview(cutoff, fromDate)
            setPreview(p)
            if (!fromDate && p.from_date) setFromDate(p.from_date)   // show the resolved fiscal-year start
        } catch { /* on the error bus */ }
        finally { setLoading(false) }
    }
    const commit = async (rail: SyncRail) => {
        setBusy(true)
        try { await apiCommitSwishSync(cutoff, fromDate, rail); await loadPreview() } catch { /* on the error bus */ }
        finally { setBusy(false) }
    }
    const toggle = (date: string) => setExpanded(prev => {
        const next = new Set(prev)
        if (next.has(date)) next.delete(date); else next.add(date)
        return next
    })

    const days = preview?.days ?? []
    const bookCount = days.filter(d => d.decision === 'book').length
    const payouts = preview?.payouts ?? []
    const kassaDays = preview?.kassa_days ?? []
    const cardCount = payouts.filter(p => p.decision === 'book').length
    const kassaCount = kassaDays.filter(k => k.decision === 'book').length
    const waiting = preview?.card_waiting

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.75rem', padding: '0.5rem', alignItems: 'flex-start' }}>
            <p style={{ margin: 0, color: '#555' }}>
                Läser banken, Visma och Zettle för perioden (förvalt hela verksamhetsåret) och visar vad som
                skulle bokföras i tre delar: Swish per bankdag, kort per utbetalning från Zettle och kassa per
                säljdag. Inget bokförs i förhandsgranskningen — varje del har sin egen knapp. Klicka på en rad
                för att se vad den innehåller.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <DateInput id="swish-from" label="Fr.o.m." value={fromDate} disabled={loading || busy} onChange={setFromDate}/>
                <DateInput id="swish-cutoff" label="T.o.m." value={cutoff} disabled={loading || busy} onChange={setCutoff}/>
                <Button variant="outlined" disabled={loading || busy} onClick={loadPreview}>Förhandsgranska</Button>
                {(loading || busy) && <CircularProgress size="1.2em"/>}
            </div>
            {preview !== null && !preview.configured &&
                <Alert severity="info">Swish är inte konfigurerat för föreningen. Ställ in Swish och Zettle under Inställningar → Företag, och kontrollera att <code>zettle.json</code> finns.</Alert>}
            {preview !== null && preview.configured &&
                <RailHeading title="Swish" count={bookCount} disabled={loading || busy} onBook={() => commit('swish')}
                    explanation="En rad per bankdag med allt banken bokförde den dagen — en helgs betalningar hamnar på måndagen."/>}
            {preview !== null && preview.configured && days.length === 0 && !loading &&
                <div>Inga Swish-betalningar i perioden.</div>}
            {preview !== null && preview.configured && days.length > 0 &&
                <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Datum</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Belopp</TableCell>
                                <TableCell>Fördelning</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {days.map(day => {
                                const hasDetail = day.deposits.length > 0 || day.purchases.length > 0
                                const isOpen = expanded.has(day.date)
                                return (
                                    <React.Fragment key={day.date}>
                                        <TableRow hover sx={hasDetail ? { cursor: 'pointer' } : undefined}
                                            onClick={() => hasDetail && toggle(day.date)}>
                                            <TableCell>
                                                <span style={{ display: 'inline-block', width: '1.2em', color: '#888' }}>
                                                    {hasDetail ? (isOpen ? '▾' : '▸') : ''}
                                                </span>
                                                {day.date}
                                                {batchCovers(day) &&
                                                    <span style={{ color: '#777', marginLeft: '0.5em', fontSize: '0.85em' }}>
                                                        {batchCovers(day)}
                                                    </span>}
                                            </TableCell>
                                            <TableCell>
                                                <span style={{ color: DECISION[day.decision].color, fontWeight: 600 }}>
                                                    {DECISION[day.decision].label}
                                                </span>
                                                {day.flags.length > 0 &&
                                                    <span style={{ color: '#c62828', fontSize: '0.8em', marginLeft: '0.5rem' }}>
                                                        ⚠ {day.flags.length}
                                                    </span>}
                                            </TableCell>
                                            <TableCell align="right">{day.total > 0 ? formatCurrency(day.total) : '–'}</TableCell>
                                            <TableCell>{day.split.map((s, i) => (
                                                <span key={i} title={s.in_scope ? undefined : 'Utanför ditt ansvarsområde'}
                                                      style={{ color: s.in_scope ? undefined : '#bbb' }}>
                                                    {i > 0 && ', '}{s.income_account}: {formatCurrency(s.amount)}
                                                </span>
                                            ))}
                                            {day.decision === 'book' && day.fee !== null &&
                                                <span style={{ color: '#777' }}> · Swish-avgift {formatCurrency(day.fee)}</span>}
                                            </TableCell>
                                        </TableRow>
                                        {isOpen &&
                                            <TableRow>
                                                <TableCell/>
                                                <TableCell colSpan={3} sx={{ py: 0 }}><DayDetail day={day}/></TableCell>
                                            </TableRow>}
                                    </React.Fragment>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>}
            {preview !== null && preview.configured && <>
                <RailHeading title="Kort" count={cardCount} disabled={loading || busy} onBook={() => commit('kort')}
                    explanation="En rad per utbetalning från Zettle, bokförd den dag den kom in på banken."/>
                {preview.ledger_error && <Alert severity="warning">{preview.ledger_error}</Alert>}
                {payouts.length > 0
                    ? <PayoutTable payouts={payouts} expanded={expanded} toggle={toggle}/>
                    : !preview.ledger_error && <div>Inga utbetalningar från Zettle i perioden.</div>}
                {waiting && waiting.count > 0 &&
                    <div style={{ color: '#ed6c02', fontSize: '0.9em' }}>
                        {waiting.count} kortbetalningar ({formatCurrency(waiting.amount)}) väntar på nästa utbetalning från Zettle.
                    </div>}
                <RailHeading title="Kassa" count={kassaCount} disabled={loading || busy} onBook={() => commit('kassa')}
                    explanation="En rad per säljdag: kontanter, Swish-köp som återbetalats ur kassan och inlösta presentkort."/>
                {kassaDays.length > 0
                    ? <KassaTable days={kassaDays} expanded={expanded} toggle={toggle}/>
                    : <div>Ingen kassa att bokföra i perioden.</div>}
            </>}
        </div>
    )
}

export default SwishSync
